import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { List, Map } from 'immutable';
import classNames from 'classnames';

import { MOBILE_BREAKPOINT, PRODUCTS, SPORT_URL_NAMES } from '@xanadu/ui-shared/constants';

import PopularMarkets from '@xanadu/ui-shared/components/PopularMarkets';
import MBZeroInfoPanel from '@xanadu/ui-shared/components/MBZeroInfoPanel';
import QuickLinks from '@xanadu/ui-shared/components/QuickLinks';
import Switcher from '@xanadu/ui-shared/components/Switcher';
import Carousel from '@xanadu/ui-shared/components/Carousel';
import SubCategory from '@xanadu/ui-shared/components/GlobalEventsPage/SubCategory';
import EventsGroup from '@xanadu/ui-shared/components/GlobalEventsPage/EventsGroup';
import MBZeroSportTiles from '@xanadu/ui-shared/components/MBZeroSportTiles';
import useVisibleEventIds from '@xanadu/ui-shared/components/GlobalEventsPage/RacingSportPage/hooks/useVisibleEventIds';
import RaceCards from '@xanadu/ui-shared/components/RaceCards';
import GlobalBannerPortal from '@xanadu/ui-shared/components/GlobalBannerPortal';
import PreCannedBetBuilders from '@xanadu/ui-shared/components/PreCannedBetBuilders';
import { usePolling } from '@xanadu/ui-shared/hooks';
import { ALL_MARKET_TYPES } from '@xanadu/ui-shared/components/GlobalEventsPage/settings';

import PopularSports from '../../../containers/homepage/PopularSports/PopularSports';

import styles from './Homepage.module.scss';

const MBZERO_PAGE_TITLE = 'Matchbook Zero | Zero Percent Commission & Zero Margin Betting';
const MBZERO_PAGE_DESCRIPTION = 'Try Matchbook Zero today. Zero Percent Commission. Zero Margin. Get the best price on your bets with Matchbook Zero.';
const EMPTY_LIST = new List();
const POPULAR_CATEGORIES = 5;

const POLLING_INTERVAL = 30000;

const Homepage = ({
  applicationVisible,
  history,
  widthSize,
  product,
  homepageTitle,
  homepageDescription,
  navEventIds,
  raceCardWidgetEnabled,
  requestEventsPageData,
  precannedBetslipsData,
  events: allEvents,
  tags,
}) => {
  const isExchange = product === PRODUCTS.exchange;

  const pageTitle = isExchange
    ? homepageTitle
    : MBZERO_PAGE_TITLE;

  const pageDescription = isExchange
    ? homepageDescription
    : MBZERO_PAGE_DESCRIPTION;

  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const popularCompetitions = useMemo(() => {
    if (isExchange) {
      return [];
    }
    const currentCategory = tags.find(tag => tag.url === '/soccer') || {};

    const { children = [] } = currentCategory;
    const subCategories = new List(children.map(metaTagId => (
      tags.find(t => t.uuid === metaTagId)
    ) || {}));
    const groupedTags = subCategories.groupBy(({ type }) => type);
    const competitions = groupedTags.get('COMPETITION') || EMPTY_LIST;
    return competitions.size ? competitions : subCategories;
  }, [isExchange, tags]);

  const eventsChildren = ['horse-racing'];
  const eventsChildrensHash = eventsChildren.join('_');
  const marketTypes = ['outright', 'outright_ded_fact'].join(',');

  const {
    visibleEventIds,
    loading
  } = useVisibleEventIds(navEventIds, eventsChildrensHash);

  const precannedData = useMemo(() => (
    precannedBetslipsData.filter(precanned => {
      const eventId = precanned.event.id;
      const { inRunningFlag, status } = allEvents.get(eventId) || {};
      return !inRunningFlag && !['closed', 'unavailable'].includes(status);
    })
  ), [precannedBetslipsData, allEvents]);

  const tagUrlNamesList = new List(
    popularCompetitions
      .slice(0, POPULAR_CATEGORIES)
      .map(({ urlName: subCategoryUrlName }) => (
        `soccer|${subCategoryUrlName}`
      )
    )
  );

  const tagUrlNamesListHash = tagUrlNamesList.join('_');

  usePolling(
    ({ pageReturn = false } = {}) => {
      if (!isExchange && tagUrlNamesListHash) {
        requestEventsPageData({
          tagUrlNamesList,
          marketTypes: ALL_MARKET_TYPES.join(','),
          id: 'soccer',
          onlyEventsWithin48Hours: true,
          offset: 0,
          perPage: 25
        });
      }
    },
    applicationVisible,
    POLLING_INTERVAL,
    [isExchange, tagUrlNamesListHash]
  );

  usePolling(
    ({ pageReturn = false } = {}) => {
      if (!isExchange) {
        requestEventsPageData({
          tagUrlNamesList: new List(eventsChildren),
          marketTypes,
          id: eventsChildren[0],
          perPage: 3,
          offset: 0,
          perTagUrlNames: 3
        });
      }
    },
    applicationVisible,
    POLLING_INTERVAL,
    [isExchange]
  );

  useEffect(() => {
    if (!isExchange && tagUrlNamesListHash) {
      requestEventsPageData({
        tagUrlNamesList,
        marketTypes: ALL_MARKET_TYPES.join(','),
        id: 'soccer',
        onlyEventsWithin48Hours: true,
        offset: 0,
        perPage: 25
      });
    }
  }, [isExchange, tagUrlNamesListHash]);

  useEffect(() => {
    if (!isExchange) {
      // For horse-racing events
      requestEventsPageData({
        tagUrlNamesList: new List(eventsChildren),
        marketTypes,
        id: eventsChildren[0],
        perPage: 3,
        offset: 0,
        perTagUrlNames: 3
      });
    }
  }, [isExchange]);

  const displayQuickSports = useMemo(() => {
    const grouped = tags.groupBy(({ urlName }) => urlName);
    const soccerTag = grouped.get(SPORT_URL_NAMES.soccer);
    const horseRacingTag = grouped.get(SPORT_URL_NAMES.horseRacing);

    return !isExchange && isMobile && !!soccerTag && !!horseRacingTag;
  }, [isExchange, isMobile, tags]);

  const handleNavigate = ({ url }) => {
    history.push(url);
  };

  const contentClassNames = classNames({
    [styles.content]: true,
    [styles.quickLinksActive]: !isExchange && isMobile && displayQuickSports,
  });

  return (
    <div className={styles.main}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="apple-itunes-app"
          content="app-id=503101282"
          app-argument="https://www.matchbook.com/"
        />
        <meta
          name="description"
          content={pageDescription}
        />
      </Helmet>
      {displayQuickSports && (
        <div className={styles.quickSports}>
          <MBZeroSportTiles navigateFunction={handleNavigate} size="small" />
        </div>
      )}
      <div className={contentClassNames}>
        <GlobalBannerPortal />
        <div className={styles.containerBanners}>
          <Carousel pageType="Homepage" />
        </div>
        {isMobile && !isExchange && <MBZeroInfoPanel />}
        {(isExchange && isMobile) && <PopularSports />}
        <RaceCards raceCardWidgetEnabled={raceCardWidgetEnabled} />
        {isExchange && (
          <PreCannedBetBuilders data={precannedData} />
        )}
        {isExchange && <PopularMarkets />}
        {!isExchange && (
          <Fragment>
            {popularCompetitions
              .slice(0, POPULAR_CATEGORIES)
              .map(({ uuid, url = '', name: subCategoryName }) => (
                <SubCategory
                  key={uuid}
                  name={subCategoryName}
                  segments={url.split('/').filter(s => s)}
                  layout="one-x-two"
                  onlyEventsWithin48Hours
                  isMBZero
                />
              ))}
            <EventsGroup
              tags={eventsChildren}
              layout="outright"
              eventIds={visibleEventIds}
              loading={loading}
              isOutright
              noHeader
            />
          </Fragment>
        )}
      </div>
      <div className={styles.widgets}>
        {isExchange && (
          <Fragment>
            <Switcher />
            <QuickLinks />
          </Fragment>
        )}
        {!isExchange && !isMobile && <MBZeroInfoPanel />}
      </div>
    </div>
  );
};

Homepage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
  product: PropTypes.string.isRequired,
  homepageTitle: PropTypes.string,
  homepageDescription: PropTypes.string,
  widthSize: PropTypes.number.isRequired,
  raceCardWidgetEnabled: PropTypes.bool,
  applicationVisible: PropTypes.bool.isRequired,
  tags: PropTypes.instanceOf(List).isRequired,
  navEventIds: PropTypes.instanceOf(Map).isRequired,
  requestEventsPageData: PropTypes.func.isRequired,
  precannedBetslipsData: PropTypes.instanceOf(List).isRequired,
  events: PropTypes.instanceOf(Map),
};

export default Homepage;
